/**
 * Parent → dtrader-c session handoff via **URL query** — the only auth channel in this deployment
 * (no cookies, no postMessage bridge; any website may embed this app).
 *
 * Contract every embedding parent site uses when it loads this app (iframe `src` or top-level URL):
 *
 *   ?token=<OAuth2 access token>      (required)  Bearer for the V2 REST/OTP endpoints
 *   ?account_id=<account id>          (required)  active Options account (e.g. `DOT90004580`)
 *   ?client_id=<oauth2 client id>     (required)  `Deriv-App-ID` for REST/OTP (alphanumeric). Must be
 *                                                 the SAME app id that issued the token — a mismatched
 *                                                 app id fails auth on Deriv's side. No fallback exists.
 *   ?expires_at=<epoch seconds or ms> (optional)  token expiry hint
 *   ?logout=true                      (optional)  handled separately by `checkLogoutParam`
 *
 * Behaviour:
 *   1. Persist the session into localStorage (`auth_flow=oauth2`, `oauth2_access_token`, active
 *      account id, client id sync keys, account rows).
 *   2. Hydrate the account rows the legacy UI reads from `GET /trading/v1/options/accounts`
 *      (currency, demo/real, balance). A REST failure is non-fatal — previous rows are kept.
 *   3. Strip `token` / `expires_at` from the visible URL (credentials never stay in history).
 *
 * Account switching = the parent reloads the app with a new `account_id` (Deriv V2 removed
 * multi-account sessions; each OTP WebSocket is scoped to exactly one account).
 *
 * This runs at boot BEFORE any store/WebSocket work (`core/src/index.tsx`), for embedded and
 * top-level loads alike.
 */

import { normalizeClientAccountsShape, syncEverymanActiveLoginSessionKeys } from '../everyman-session-cookie';
import { getOptionsAccounts, TOptionsAccount } from '../options-otp-ws';

/** localStorage keys — must stay in sync with `@deriv/utils` auth-flow and the iframe bridge. */
const LS_AUTH_FLOW = 'auth_flow';
const LS_OAUTH2_ACCESS_TOKEN = 'oauth2_access_token';
const LS_OAUTH2_EXPIRES_AT = 'oauth2_access_token_expires_at';
const LS_OAUTH2_ACTIVE_ACCOUNT_ID = 'oauth2_active_account_id';
const LS_ACTIVE_LOGINID = 'active_loginid';
const LS_CLIENT_ACCOUNTS = 'client.accounts';
const LS_CLIENT_ACCOUNT_DETAILS = 'client_account_details';
const LS_CLIENT_ACCOUNTS_ALT = 'clientAccounts';
const LS_ACCOUNTS_LIST = 'accountsList';
const LS_SYNC_OAUTH_CLIENT_ID = 'everyman_sync_oauth_client_id';
const LS_SYNC_OAUTH_DERIV_APP_ID = 'everyman_sync_oauth_deriv_app_id';

/** URL query keys of the parent handoff contract (exported for parents/tests). */
export const OAUTH2_QUERY_TOKEN = 'token';
export const OAUTH2_QUERY_ACCOUNT_ID = 'account_id';
export const OAUTH2_QUERY_CLIENT_ID = 'client_id';
export const OAUTH2_QUERY_EXPIRES_AT = 'expires_at';

type TAccountRow = {
    loginid: string;
    token: string;
    currency: string;
    is_virtual: 0 | 1;
    account_type?: 'demo' | 'real';
    balance?: number;
};

const safeRead = (key: string): string => {
    try {
        return (typeof localStorage !== 'undefined' ? localStorage.getItem(key) : '') || '';
    } catch {
        return '';
    }
};

const safeWrite = (key: string, value: string): void => {
    try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
    } catch {
        /* quota / private mode */
    }
};

const safeRemove = (key: string): void => {
    try {
        if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
    } catch {
        /* ignore */
    }
};

const parseExistingRows = (): Record<string, TAccountRow> => {
    const raw = safeRead(LS_CLIENT_ACCOUNTS) || safeRead(LS_CLIENT_ACCOUNTS_ALT);
    if (!raw) return {};
    try {
        return normalizeClientAccountsShape(JSON.parse(raw)) as Record<string, TAccountRow>;
    } catch {
        return {};
    }
};

const rowFromApiAccount = (account: TOptionsAccount, token: string): TAccountRow => ({
    loginid: account.account_id,
    token,
    currency: account.currency || 'USD',
    is_virtual: account.account_type === 'demo' ? 1 : 0,
    account_type: account.account_type,
    balance: typeof account.balance === 'number' ? account.balance : 0,
});

/**
 * Fallback row when the accounts REST call failed but the parent told us which account is active.
 * Reuses the previous row when one exists so demo/real + currency survive a transient outage.
 */
const fallbackRow = (loginid: string, token: string, previous?: TAccountRow): TAccountRow => ({
    loginid,
    token,
    currency: previous?.currency || 'USD',
    is_virtual: previous?.is_virtual ?? (/^VR/i.test(loginid) ? 1 : 0),
    account_type: previous?.account_type,
    balance: previous?.balance ?? 0,
});

const persistSession = (session: {
    access_token: string;
    account_id: string;
    client_id: string;
    expires_at_ms?: number;
}): void => {
    safeWrite(LS_AUTH_FLOW, 'oauth2');
    safeWrite(LS_OAUTH2_ACCESS_TOKEN, session.access_token);
    if (session.expires_at_ms) {
        safeWrite(LS_OAUTH2_EXPIRES_AT, String(session.expires_at_ms));
    } else {
        safeRemove(LS_OAUTH2_EXPIRES_AT);
    }
    if (session.client_id) {
        // Feeds `getOAuthDerivAppIdFromEverymanSync` → Deriv-App-ID header for REST + OTP.
        safeWrite(LS_SYNC_OAUTH_CLIENT_ID, session.client_id);
        safeWrite(LS_SYNC_OAUTH_DERIV_APP_ID, session.client_id);
    }
    if (session.account_id) {
        safeWrite(LS_OAUTH2_ACTIVE_ACCOUNT_ID, session.account_id);
        safeWrite(LS_ACTIVE_LOGINID, session.account_id);
    }
};

const persistAccountRows = (rows: Record<string, TAccountRow>, active_loginid: string): void => {
    const row_values = Object.values(rows);
    const token_map: Record<string, string> = {};
    row_values.forEach(row => {
        token_map[row.loginid] = row.token;
    });

    safeWrite(LS_CLIENT_ACCOUNTS, JSON.stringify(rows));
    safeWrite(LS_CLIENT_ACCOUNTS_ALT, JSON.stringify(rows));
    // Array shape consumed by `socket-general.buildSyntheticOauth2AuthorizeResponse` (account_list).
    safeWrite(LS_CLIENT_ACCOUNT_DETAILS, JSON.stringify(row_values));
    safeWrite(LS_ACCOUNTS_LIST, JSON.stringify(token_map));
    safeWrite(LS_ACTIVE_LOGINID, active_loginid);
    safeWrite(LS_OAUTH2_ACTIVE_ACCOUNT_ID, active_loginid);
    syncEverymanActiveLoginSessionKeys();
};

/**
 * Fetch the session's Options accounts and merge them into the localStorage rows the legacy UI reads.
 * Never throws: a REST failure keeps the previous rows (+ a minimal row for `requested_account_id`).
 */
const hydrateAccountRows = async (token: string, requested_account_id: string): Promise<void> => {
    const existing = parseExistingRows();
    let rows: Record<string, TAccountRow> = existing;
    let fetched = false;

    try {
        const accounts = await getOptionsAccounts(token);
        if (accounts.length) {
            fetched = true;
            rows = {};
            accounts.forEach(account => {
                if (!account?.account_id) return;
                rows[account.account_id] = rowFromApiAccount(account, token);
            });
        }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[dtrader-c][oauth2-query-auth] accounts REST failed — using previous rows:', {
            message: e instanceof Error ? e.message : String(e),
        });
    }

    if (!fetched && requested_account_id && !rows[requested_account_id]) {
        rows[requested_account_id] = fallbackRow(requested_account_id, token, existing[requested_account_id]);
    }

    const active_loginid =
        (requested_account_id && rows[requested_account_id] && requested_account_id) ||
        safeRead(LS_OAUTH2_ACTIVE_ACCOUNT_ID) ||
        Object.keys(rows)[0] ||
        requested_account_id;

    if (!active_loginid || !rows[active_loginid]) {
        // eslint-disable-next-line no-console
        console.error(
            '[dtrader-c][oauth2-query-auth] no Options account could be resolved — parent must pass ?account_id=… or the REST call must succeed'
        );
        return;
    }

    persistAccountRows(rows, active_loginid);
};

/**
 * Apply the parent query handoff, if present. Returns `true` when a session was applied.
 * Safe to call on every boot: no-ops unless `?token=…` exists.
 */
export const applyOAuth2AuthFromQuery = async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false;

    let search_params: URLSearchParams;
    try {
        search_params = new URLSearchParams(window.location.search || '');
    } catch {
        return false;
    }

    const access_token = (search_params.get(OAUTH2_QUERY_TOKEN) || '').trim();
    if (!access_token) return false;

    const account_id = (search_params.get(OAUTH2_QUERY_ACCOUNT_ID) || '').trim();
    const client_id_raw = (search_params.get(OAUTH2_QUERY_CLIENT_ID) || '').trim();
    const client_id = /^[A-Za-z0-9]+$/.test(client_id_raw) ? client_id_raw : '';
    if (!client_id) {
        // eslint-disable-next-line no-console
        console.error(
            '[dtrader-c][oauth2-query-auth] missing or invalid ?client_id= — every embedding site must pass ' +
                'the same OAuth2 app id that issued the access token (no hard-coded fallback exists). ' +
                'REST/OTP calls will fail until the parent supplies it.'
        );
    }
    const expires_at_raw = (search_params.get(OAUTH2_QUERY_EXPIRES_AT) || '').trim();
    const expires_at_parsed = expires_at_raw ? Number(expires_at_raw) : undefined;
    const expires_at_ms =
        typeof expires_at_parsed === 'number' && Number.isFinite(expires_at_parsed) ? expires_at_parsed : undefined;

    persistSession({
        access_token,
        account_id,
        client_id,
        expires_at_ms,
    });

    // Strip credentials from the visible URL immediately (before any async work / render).
    search_params.delete(OAUTH2_QUERY_TOKEN);
    search_params.delete(OAUTH2_QUERY_EXPIRES_AT);
    try {
        const qs = search_params.toString();
        window.history.replaceState(
            {},
            '',
            `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`
        );
    } catch {
        /* history API unavailable — non-fatal */
    }

    // eslint-disable-next-line no-console
    console.info('[dtrader-c][oauth2-query-auth] session applied from parent query', {
        token_len: access_token.length,
        has_account_id: !!account_id,
        has_client_id: !!client_id,
    });

    await hydrateAccountRows(access_token, account_id);
    return true;
};
